"use client";

import { c as _c } from "react/compiler-runtime";
import { Info } from "lucide-react";
import { Tooltip as TooltipPrimitive } from "radix-ui";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "~/components/ui/select";
import { VEHICLE_SIZES, type VehicleType } from "~/lib/constants/vehicle";
import { cn } from "~/lib/utils";
export type VehicleSize = VehicleType;
const SIZE_DESCRIPTIONS: Record<VehicleSize, string> = {
  motorcycle: "Motorcycles & scooters",
  subcompact: "Small hatchbacks (e.g. Mini Cooper, Fiat 500)",
  compact: "Small cars (e.g. Honda Civic, Toyota Corolla)",
  standard: "Mid-size sedans & coupes (e.g. Camry, Accord)",
  suv: "SUVs & crossovers (e.g. RAV4, CR-V)",
  truck: "Pickup trucks (e.g. F-150, Silverado)",
  oversized: "Large trucks, vans & RVs"
};
interface VehicleSizePickerProps {
  value: VehicleSize | null;
  onChange: (size: VehicleSize) => void;
  showLabel?: boolean;
}
export function VehicleSizePicker(t0) {
  const $ = _c(18);
  const {
    value,
    onChange,
    showLabel: t1
  } = t0;
  const showLabel = t1 === undefined ? true : t1;
  let t2;
  if ($[0] !== showLabel) {
    t2 = showLabel && <div className="flex items-center gap-1.5"><span className="text-[10px] font-bold uppercase tracking-[0.09em] text-secondary">Vehicle Size</span><TooltipPrimitive.Provider delayDuration={200}><TooltipPrimitive.Root><TooltipPrimitive.Trigger asChild={true}><button type="button" className="text-muted hover:text-secondary transition-colors focus:outline-none"><Info className="h-3 w-3" /></button></TooltipPrimitive.Trigger><TooltipPrimitive.Portal><TooltipPrimitive.Content side="right" className="z-[200] max-w-[200px] rounded-xl border border-border bg-white px-3 py-2 shadow-xl text-left"><ul className="space-y-1">{VEHICLE_SIZES.map(_temp)}</ul><TooltipPrimitive.Arrow className="fill-white" /></TooltipPrimitive.Content></TooltipPrimitive.Portal></TooltipPrimitive.Root></TooltipPrimitive.Provider></div>;
    $[0] = showLabel;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const t3 = value ?? "";
  let t4;
  if ($[2] !== onChange) {
    t4 = v => onChange(v as VehicleSize);
    $[2] = onChange;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  let t5;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <SelectTrigger className="w-full rounded-lg border-border bg-surface text-sm text-body focus:ring-accent"><SelectValue placeholder="Select size" /></SelectTrigger>;
    $[4] = t5;
  } else {
    t5 = $[4];
  }
  let t6;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <SelectContent>{VEHICLE_SIZES.map(_temp2)}</SelectContent>;
    $[5] = t6;
  } else {
    t6 = $[5];
  }
  let t7;
  if ($[6] !== t3 || $[7] !== t4) {
    t7 = <div className="block sm:hidden w-full"><Select value={t3} onValueChange={t4}>{t5}{t6}</Select></div>;
    $[6] = t3;
    $[7] = t4;
    $[8] = t7;
  } else {
    t7 = $[8];
  }
  let t8;
  if ($[9] !== onChange || $[10] !== value) {
    t8 = VEHICLE_SIZES.map(t9 => {
      const {
        value: size_1,
        label: label_1
      } = t9;
      const isActive = value === size_1;
      return <button key={size_1} type="button" onClick={() => onChange(size_1 as VehicleSize)} className={cn("h-9 px-4 rounded-full text-[13px] font-medium transition-all border", isActive ? "bg-[#374b6d] text-white border-[#374b6d] shadow-sm" : "bg-surface-2 text-secondary border-border hover:border-accent/50 hover:text-body")}>{label_1}</button>;
    });
    $[9] = onChange;
    $[10] = value;
    $[11] = t8;
  } else {
    t8 = $[11];
  }
  let t9;
  if ($[12] !== t8) {
    t9 = <div className="hidden sm:flex flex-row flex-wrap gap-2">{t8}</div>;
    $[12] = t8;
    $[13] = t9;
  } else {
    t9 = $[13];
  }
  let t10;
  if ($[14] !== t2 || $[15] !== t7 || $[16] !== t9) {
    t10 = <div className="flex flex-col gap-2">{t2}{t7}{t9}</div>;
    $[14] = t2;
    $[15] = t7;
    $[16] = t9;
    $[17] = t10;
  } else {
    t10 = $[17];
  }
  return t10;
}
function _temp2(t0) {
  const {
    value: size_0,
    label: label_0
  } = t0;
  return <SelectItem key={size_0} value={size_0}>{label_0}</SelectItem>;
}
function _temp(t0) {
  const {
    value: size,
    label
  } = t0;
  return <li key={size} className="text-[10px] text-slate-600"><span className="font-bold text-slate-900">{label}:</span>{" "}{SIZE_DESCRIPTIONS[size as VehicleSize]}</li>;
}